import { IPCEventType } from '@electron/shared/ipc.types';
import {
  LbNotificationCommands,
  LbNotificationEvents,
  type LbNotification,
  type NativeNotification,
  type NotificationActionEvent,
  type NotificationInteractionEvent,
} from '@electron/shared/lb-notification.types';

import { ElectronAPI } from '../base/ElectronAPI';
import {
  type ElectronListenerMethod,
  type ElectronUnsubscribe,
} from '../base/types';

import type { NotificationSoundChoice } from '@electron/shared/sounds.types';

/**
 * Renderer-side API for interacting with the Littlebird notification system.
 *
 * This API manages Littlebird notifications across the panel queue and native
 * OS notification surfaces.
 *
 * @example
 * ```typescript
 * // Send a notification
 * await electronApis.lbNotifications.sendNotification({
 *   id: 'notification-123',
 *   view: 'upcoming-meeting',
 *   payload: { meetingId: 'meeting-456' }
 * });
 *
 * await electronApis.lbNotifications.native.sendNotification({
 *   id: 'notification-456',
 *   title: 'Task complete',
 *   message: 'Your task has finished successfully',
 *   sound: 'done',
 * });
 * ```
 */
export class LbNotificationsAPI extends ElectronAPI {
  readonly native = {
    sendNotification: async (notification: NativeNotification) =>
      this.invoke(
        IPCEventType.lbNotificationsSendNativeNotification,
        notification
      ),
  };

  /**
   * Sends a panel notification to the main process to be queued and displayed.
   *
   * The notification will be added to the persistent queue. If no notification
   * is currently being shown, it will be displayed immediately. Otherwise, it
   * will be shown after the current notification is dismissed.
   *
   * @param notification - The notification object to send
   * @returns A promise that resolves when the notification has been queued
   */
  async sendNotification(notification: LbNotification) {
    return this.invoke(
      IPCEventType.lbNotificationsQueueNotification,
      notification
    );
  }

  /**
   * Listens for incoming notifications from the main process.
   *
   * Sets up a listener for the WillNotify event that fires when a notification
   * is about to be displayed.
   *
   * @param callback - Function to call when a notification is received
   * @returns Unsubscribe function to remove the listener
   *
   * @example
   * ```typescript
   * const unsubscribe = await electronApis.lbNotifications.listen((notification) => {
   *   console.log('Received notification:', notification);
   * });
   *
   * // Later, to stop listening:
   * unsubscribe();
   * ```
   */
  async listen(
    callback: (notification: LbNotification) => void
  ): Promise<ElectronUnsubscribe> {
    return this.addListener<LbNotification>({
      eventType: IPCEventType.lbNotificationsWillNotify,
      callback: (notification) => callback(notification),
    });
  }

  ready(): void {
    this.ipcRenderer?.send(LbNotificationEvents.Ready);
  }

  /**
   * Notifies the main process that the notification has been presented to the user.
   *
   * Called when the notification animation completes and becomes fully visible.
   * This triggers the notification sound to play.
   *
   * @param notification - The notification that was presented
   * @returns A promise that resolves when the event has been handled
   *
   * @example
   * ```typescript
   * // When notification animation completes
   * await electronApis.lbNotifications.presented(notification);
   * ```
   */
  async presented(notification: LbNotification): Promise<void> {
    return this.invoke(LbNotificationEvents.DidPresent, notification);
  }

  /**
   * Notifies the main process that the user has acknowledged/dismissed the notification.
   *
   * This clears the current notification and triggers the main process to advance
   * to the next notification in the queue or destroy the notification panel if
   * no more notifications are pending.
   *
   * @param notificationId - The notification being acknowledged. The main process
   * ignores the call when this no longer matches what is on screen, so a dismissal
   * that lands after the panel moved on cannot discard the newer notification.
   * @returns A promise that resolves when the notification has been acknowledged
   */
  async notified(notificationId?: string): Promise<void> {
    return this.invoke(LbNotificationEvents.DidNotify, notificationId);
  }

  /**
   * Clears all pending notifications from the queue.
   *
   * This dismisses the current notification and clears all queued notifications,
   * then destroys the notification panel.
   *
   * @returns A promise that resolves when all notifications have been cleared
   *
   * @example
   * ```typescript
   * // When user clicks "Clear All" button
   * await electronApis.lbNotifications.clearAll();
   * ```
   */
  async clearAll(): Promise<void> {
    return this.invoke(LbNotificationCommands.ClearAll);
  }

  /**
   * Gets the count of queued notifications (excluding current notification).
   *
   * @returns A promise that resolves with the number of notifications waiting in the queue
   *
   * @example
   * ```typescript
   * const count = await electronApis.lbNotifications.getQueueCount();
   * console.log(`${count} notifications waiting`);
   * ```
   */
  async getQueueCount(): Promise<number> {
    return this.invoke(LbNotificationCommands.QueueCount);
  }

  /**
   * Checks whether a notification with the given ID is currently tracked.
   *
   * This checks both the active notification and queued notifications.
   *
   * @param notificationId - Notification ID to check
   * @returns A promise that resolves to true when the notification exists
   */
  async hasNotification(notificationId: string): Promise<boolean> {
    return this.invoke(LbNotificationCommands.HasNotification, notificationId);
  }

  /**
   * Removes queued notifications with a matching ID.
   *
   * This only affects queued items and never dismisses the currently displayed notification.
   *
   * @param notificationId - Notification ID to remove from the queue
   * @returns Number of queued notifications removed
   */
  async removeQueuedById(notificationId: string): Promise<number> {
    return this.invoke(LbNotificationCommands.RemoveQueuedById, notificationId);
  }

  /**
   * Dismisses a notification by ID, whether queued or currently displayed.
   *
   * If the notification is currently displayed, it triggers a dismiss request.
   * If queued, it removes it from the queue. Returns true if a notification was found.
   *
   * @param notificationId - Notification ID to dismiss
   * @returns Whether a notification was found and dismissed
   */
  async dismissById(notificationId: string): Promise<boolean> {
    return this.invoke(LbNotificationCommands.DismissById, notificationId);
  }

  /**
   * Emits a notification action chosen by the user.
   *
   * Used by both the panel UI and native notification action handlers.
   */
  sendAction(action: NotificationActionEvent): void {
    this.ipcRenderer?.send(LbNotificationCommands.NotificationAction, action);
  }

  /**
   * Reports whether the user is currently interacting with the notification on
   * screen.
   *
   * The main process uses this to hold an expedited notification back for a
   * bounded time rather than pulling the panel out from under the pointer.
   */
  interactionChanged(interaction: NotificationInteractionEvent): void {
    this.ipcRenderer?.send(
      LbNotificationCommands.InteractionChanged,
      interaction
    );
  }

  /**
   * Subscribes to notification action events from the main process.
   */
  async onAction(
    callback: (action: NotificationActionEvent) => void
  ): Promise<ElectronUnsubscribe> {
    return this.addListener<NotificationActionEvent>({
      eventType: IPCEventType.lbNotificationsAction,
      callback: (action) => callback(action),
    });
  }

  /**
   * Listens for queue count changes.
   *
   * Sets up a listener for the QueueChanged event that fires when notifications
   * are added or removed from the queue.
   *
   * @param callback - Function to call with the new queue count
   * @returns Unsubscribe function to remove the listener
   *
   * @example
   * ```typescript
   * const unsubscribe = await electronApis.lbNotifications.onQueueChanged((count) => {
   *   console.log(`Queue now has ${count} notifications`);
   * });
   *
   * // Later, to stop listening:
   * unsubscribe();
   * ```
   */
  async onQueueChanged(
    callback: (count: number) => void
  ): Promise<ElectronUnsubscribe> {
    return this.addListener<number>({
      eventType: IPCEventType.lbNotificationsQueueChanged,
      callback: (count) => callback(count),
    });
  }

  /**
   * Gets the current notification sound volume.
   *
   * @returns A promise that resolves with the volume level (0.0 to 1.0)
   *
   * @example
   * ```typescript
   * const volume = await electronApis.lbNotifications.getVolume();
   * console.log(`Current volume: ${volume * 100}%`);
   * ```
   */
  async getVolume(): Promise<number> {
    return this.invoke(LbNotificationCommands.GetVolume);
  }

  /**
   * Gets whether Littlebird notifications are enabled.
   *
   * @returns A promise that resolves with the enabled flag
   */
  async getEnabled(): Promise<boolean> {
    return this.invoke(LbNotificationCommands.GetEnabled);
  }

  /**
   * Sets the notification sound volume.
   *
   * @param volume - Volume level from 0.0 (silent) to 1.0 (full volume)
   * @returns A promise that resolves when the volume has been set
   *
   * @example
   * ```typescript
   * await electronApis.lbNotifications.setVolume(0.5);
   * ```
   */
  async setVolume(volume: number): Promise<void> {
    return this.invoke(LbNotificationCommands.SetVolume, volume);
  }

  /**
   * Enables or disables Littlebird notifications.
   *
   * When disabled, the main process clears all queued notifications and closes
   * the notification panel.
   *
   * @param enabled - Whether notifications should be shown
   */
  async setEnabled(enabled: boolean): Promise<void> {
    return this.invoke(LbNotificationCommands.SetEnabled, enabled);
  }

  /** The app's own `attention` cue, or a macOS system sound in its place */
  async setSoundChoice(soundChoice: NotificationSoundChoice): Promise<void> {
    return this.invoke(LbNotificationCommands.SetSoundChoice, soundChoice);
  }

  async previewSoundChoice(
    soundChoice: NotificationSoundChoice
  ): Promise<void> {
    return this.invoke(LbNotificationCommands.PreviewSoundChoice, soundChoice);
  }

  /**
   * Listens for requests to dismiss the current notification.
   *
   * Sets up a listener for the RequestDismissNotification event that fires when
   * the main process requests the notification to be dismissed (e.g., due to timeout
   * or external dismissal).
   *
   * @param callback - Function to call when a dismiss request is received
   * @returns Unsubscribe function to remove the listener
   *
   * @example
   * ```typescript
   * const unsubscribe = await electronApis.lbNotifications.listenRequestDismissNotification(() => {
   *   console.log('Notification dismiss requested');
   * });
   *
   * // Later, to stop listening:
   * unsubscribe();
   * ```
   */
  listenRequestDismissNotification: ElectronListenerMethod<IPCEventType.lbNotificationsRequestDismissNotification> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.lbNotificationsRequestDismissNotification,
        callback,
      });
    };

  /**
   * Removes an event listener for a specific IPC event type.
   *
   * @param eventType - The IPC event type to stop listening to
   */
  removeListener(eventType: IPCEventType): void {
    super.removeListener(eventType);
  }
}
